import styled, { css } from 'styled-components';
import {
  compose,
  color,
  ColorProps,
  display,
  DisplayProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
  shadow,
  ShadowProps,
  borders,
  BordersProps,
  layout,
  LayoutProps,
  flexbox,
  FlexboxProps,
  border,
  zIndex,
  ZIndexProps,
  position,
  PositionProps
} from 'styled-system';
import { setFormInputStyle } from '../Form.style';

/**
 * Components.
 */
import Typography from '../../commonComponents/Typography/Typography';

// styled-system custom intersection type
export type StyledDropdownProps = ColorProps &
  SpaceProps &
  TypographyProps &
  ShadowProps &
  DisplayProps &
  BordersProps &
  FlexboxProps &
  ZIndexProps &
  DisplayProps &
  PositionProps &
  LayoutProps & {
    enableSupportPageStyle?: boolean;
    isActiveProp?: boolean;
    hasError?: boolean;
  };

interface IButtonTextProps {
  isPlaceholder: boolean;
  enableSupportPageStyle?: boolean;
}

// Constants
const DD_MAX_HEIGHT = '180px';
const DD_CHEVRON_HEIGHT = '16px';

// Dropdown button element
const DropdownMenuButton = styled.button<StyledDropdownProps>`
  ${({ hasError, theme, isActiveProp, enableSupportPageStyle }) => `
    ${setFormInputStyle(hasError, theme)}
    ${
      enableSupportPageStyle
      ? `
        border-radius: ${theme.uptimeSpace[2]}px;
        min-height: 38px !important;
        align-items: center;
        border-color: ${
          hasError
          ? theme.uptimeColors.generalColors.state.danger.light
          : theme.uptimeColors.grayScales.gray
        };
        background-color: transparent !important;
        padding-left: ${theme.uptimeSpace[12]}px !important;
        padding-right:${theme.uptimeSpace[4]}px !important;
        &:hover{
          border: 1px solid ${theme.uptimeColors.grayScales.black};
        }
        &:focus{
          border: 2px solid ${theme.uptimeColors.mainTheme.secondary};
        }
      `
      :isActiveProp
        ? `border-color: ${
            hasError
              ? theme.colors.state.danger
              : theme.colors.form.input.border.focus
          };`
        : ''
    }
  `}
  ${compose(
    color,
    space,
    layout,
    display,
    flexbox,
    typography,
    shadow,
    borders
  )}
`;

type ChevronProp = Pick<StyledDropdownProps, 'isActiveProp'>;
// Dropdown chevron
const Chevron = styled.svg.attrs(() => ({
  viewBox: '0 0 32 32',
  'aria-hidden': 'true'
}))<ChevronProp>`
  height: ${DD_CHEVRON_HEIGHT};
  width: ${({ theme }) => theme.space[5]}px;
  transition: transform 0.2s;
  ${({ isActiveProp, theme }) =>
    isActiveProp &&
    css`
      transform: rotate(180deg);
      fill: ${theme.colors.form.text.input.enabled};
    `}
  ${compose(space)}
`;

/**
 * List of dropdown options.
 */
const Menu = styled.ul<StyledDropdownProps>`
  max-height: ${DD_MAX_HEIGHT};
  overflow: auto;
  ${({ theme, hasError, enableSupportPageStyle }) => `
  ${
    enableSupportPageStyle
    ? `
      border: 0 !important;
      border-radius: ${theme.uptimeSpace[2]}px !important;
    `
    : `
    border-bottom: 1px solid ${
      hasError
        ? theme.colors.state.danger
        : theme.colors.form.input.border.focus
    };`
  }
  `}
  ${compose(space, color, layout, shadow, zIndex, position)}
`;

/**
 * Text displayed in menu button.
 */
const ButtonText = styled(Typography)<IButtonTextProps>`
  width: inherit;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  ${({enableSupportPageStyle, theme}) =>`
    ${enableSupportPageStyle
      ? `
        font-size : ${theme.uptimeFontSizes.desktop.fz4}px;
      `:''}
  `}
  ${({ isPlaceholder, theme }) =>
    isPlaceholder && `color: ${theme.colors.form.text.input.placeholder};`}
`;

// Dropdown list item
const Item = styled.button<StyledDropdownProps>`
  list-style: none;
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  ${({ theme,enableSupportPageStyle }) => `
    ${
      enableSupportPageStyle ? `
        padding: ${theme.uptimeSpace[5]}px 0px ${theme.uptimeSpace[5]}px  ${theme.uptimeSpace[12]}px !important;
        &:hover{
          background: ${theme.uptimeColors.grayScales.grayishBackground};
        }
        &:focus{
          background: ${theme.uptimeColors.grayScales.lightGray};
        }
      `
      :`
        &:hover {
          background: ${theme.colors.background.hover};
          color: ${theme.colors.form.text.menu.hover};
        }
        &: focus {
          background: ${theme.colors.background.hover};
          border: 2px solid ${theme.colors.form.input.border.focus};
          padding: ${theme.space[2] - 2}px;
          color: ${theme.colors.form.text.menu.hover};
        }
      `
    }

  `}

  ${compose(typography, space, color, layout, border, layout)}
`;

// Dropdown list item text
const ItemText = styled.p<StyledDropdownProps>`
  ${({ theme }) => `
    color: ${theme.colors.form.text.input.enabled};
  `}
  ${compose(space, typography, color)}
`;

// Helper error component
const ErrorComponent = styled.small.attrs(() => ({
  m: 0,
  fontSize: '10px'
}))`
  color: ${({ theme }) => theme.colors.state.danger};
  text-decoration: underline;
  ${compose(space, typography)}
`;

// Wrapper for menu list label text
const TextWrapper = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Icon wrapper for menu list item
 */
const IconWrapper = styled.div`
  margin-right: 10px;
  & > svg {
    display: inline-block;
    vertical-align: middle;
    font-size: ${({ theme }) => theme.fontSizes[0]};
  }
`;

export {
  DropdownMenuButton,
  Menu,
  ButtonText,
  Item,
  ItemText,
  Chevron,
  ErrorComponent,
  TextWrapper,
  IconWrapper
};
